import { innerSize, padding, radius, size } from '@components/Buttons/utils';
import { InlineFlex } from '@components/Layout/Blocks';
import { color, prop } from '@lib/style/theme';
import { unitStrict } from '@lib/style/unit';
import { readableColor } from '@lib/style/utils';
import Button, { ButtonProps } from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { namedComponentRef } from '@packages/react/lib/named';
import { ColorType } from '@packages/style/colors';
import { darken, lighten, transparentize } from 'polished';
import React, { CSSProperties, ReactNode } from 'react';
import styled, { css } from 'styled-components';

export interface RoundedButtonProps extends Omit<ButtonProps, 'children' | 'color' | 'variant'> {
    children?: ReactNode;
    margin?: CSSProperties['margin'];
    padding?: CSSProperties['padding'];
    radius?: CSSProperties['borderRadius'];
    background?: ColorType;
    textColor?: ColorType;
    color?: ColorType;
    elevation?: number;
    loading?: boolean;
    ref?: any;
    variant?: ButtonProps['variant'];
    component?: any;
    to?: string;
    shadow?: boolean;
}

const RoundedButtonComponent = React.forwardRef(
    namedComponentRef<RoundedButtonProps>((props: RoundedButtonProps, ref$: any) => {
        const {
            margin,
            radius,
            color,
            size,
            background,
            padding,
            elevation,
            textColor,
            loading,
            disabled,
            children,
            ...rest
        } = props;

        if (!rest.ref) {
            rest.ref = ref$;
        }

        return (
            <Button {...rest} disabled={disabled || loading}>
                {loading ? (
                    <InlineFlex style={{ marginRight: 14 }} align={'center'}>
                        <CircularProgress size={20} style={{ color: '#000' }} />
                    </InlineFlex>
                ) : null}
                {children}
            </Button>
        );
    }, 'RoundedButton'),
);

const roundedButtonBackground = ({ variant, disabled, ...props }): any => {
    let style;
    let colorFactory: any;
    let borderFactory: any = 'none';
    let lineHeightFactory: any = size;
    const textColor: any = color('textColor')(props);
    const color$ = color('color')(props);
    const background$ = color('background')(props) ? color('background')(props) : '';
    const borderColor$ = color('borderColor')(props) ? color('borderColor')(props) : '';
    let shadow$ = props.shadow ?? false;
    const width$ = props.width ?? '';

    switch (variant) {
        case 'outlined':
            if (!disabled) {
                lineHeightFactory = innerSize;
                borderFactory = `1px solid ${color$}`;
                colorFactory = `${color$}`;

                style =
                    color$ && color$ !== 'transparent'
                        ? css`
                              &:hover {
                                  background-color: ${transparentize(0.9, color$)};
                              }
                              &:active {
                                  background-color: ${transparentize(0.8, color$)};
                              }
                          `
                        : css`
                              &:hover {
                                  background-color: ${color$};
                              }
                              &:active {
                                  background-color: ${color$};
                              }
                          `;
            } else {
                borderFactory = undefined;
                lineHeightFactory = undefined;
                colorFactory = undefined;
            }
            break;
        case 'contained':
            colorFactory =
                color$ && color$ !== 'transparent' && (!textColor || textColor !== 'transparent')
                    ? readableColor(color$)
                    : textColor;

            style =
                color$ && color$ !== 'transparent'
                    ? css`
                          background-color: ${color$};
                          &:disabled {
                              background-color: #ccc !important;
                              color: #777 !important;
                          }
                          &:hover {
                              background-color: ${lighten(0.1, color$)};
                          }
                          &:active {
                              background-color: ${darken(0.1, color$)};
                          }
                      `
                    : css`
                          &:hover {
                              background-color: ${color$};
                          }
                          &:active {
                              background-color: ${color$};
                          }
                      `;
            break;
        case 'bordered':
            if (!disabled) {
                lineHeightFactory = innerSize;
                borderFactory = `1px solid ${borderColor$}`;
                colorFactory = `${color$}`;
                shadow$ = shadow$ ? '1px 1px 1px 1px lightgrey' : '';
                style =
                    color$ && color$ !== 'transparent'
                        ? css`
                              border-radius: 3px;
                              width: ${width$};
                              box-shadow: ${shadow$};
                              background-color: ${background$};
                              &:hover {
                                  background-color: ${transparentize(0.9, background$)};
                              }
                              &:active {
                                  background-color: ${transparentize(0.8, background$)};
                              }
                          `
                        : css`
                              &:hover {
                                  background-color: ${background$};
                              }
                              &:active {
                                  background-color: ${background$};
                              }
                          `;
            } else {
                borderFactory = undefined;
                lineHeightFactory = undefined;
                colorFactory = undefined;
            }
            break;
        default:
            colorFactory = `${color$}`;
            style = css``;
    }

    return css`
        & {
            border: ${borderFactory};
            line-height: ${lineHeightFactory};
            color: ${colorFactory};
            ${style}
        }
    `;
};

export const RoundedButton = styled(RoundedButtonComponent)<RoundedButtonProps>`
    && {
        margin: ${prop('margin', 6, unitStrict)};
        box-shadow: none;
        height: ${size as any};
        border-radius: ${radius as any};
        padding: ${padding as any};
        min-width: 0;
        ${roundedButtonBackground as any}
    }
`;

RoundedButton.displayName = 'UI(MUI(Button))';
RoundedButton.defaultProps = {
    background: 'transparent',
    color: 'black',
};
